import React, { useEffect, useState, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

import { getStyles } from './styles';
import { buttonStyles } from '../../../styles/buttonStyles';
import { tostAlert } from '../../../utils/AlertToast';
import LoadBar from '../../../utils/LoadBar';
import { useAppContext } from '../../../context/AppContext';
import AmeyaSettingsApi from '../../../Api/Services/AppflyteBackend/AmeyaSettings';
import moment from 'moment';

function LLMSettings() {
    const theme = useTheme();
    const styles = getStyles(theme);

    const { selectedOrganization, selectedProject, selectedWorkspace } = useAppContext();

    const [loading, setLoading] = useState(false);
    const [llmType, setLLMType] = useState('gemini');
    const [updateKey, setUpdateKey] = useState(null);
    const [itemExists, setItemExists] = useState(false);

    const projectId = selectedProject?.payload?.__auto_id__;
    const workspaceId = selectedWorkspace?.payload?.__auto_id__;
    const organizationId = selectedOrganization?.payload?.__auto_id__;

    const fetchSettings = useCallback(async () => {
        if (!projectId) return;

        setLoading(true);
        try {
            const { status, data } = await AmeyaSettingsApi.getById(projectId);

            if (status === 200 && data) {
                setUpdateKey(data.update_key);
                setLLMType(data.payload?.llm_type || 'gemini');
                setItemExists(true);
            } else {
                setItemExists(false);
            }
        } catch (err) {
            console.error('Fetch Settings Error:', err);
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSave = async () => {
        if (!projectId) return;

        setLoading(true);
        try {

            const timestamp = moment().format('DD-MM-YYYY HH:mm:ss');

            let response;

            if (!itemExists) {
                const payload = {
                    collection_item: {
                        organization_id: organizationId,
                        workspace_id: workspaceId,
                        project_id: projectId,
                        llm_type: llmType,
                        created_on: timestamp,
                        __auto_id__: projectId
                    }
                };
                response = await AmeyaSettingsApi.add(JSON.stringify(payload));
            } else {
                const payload = {
                    id: projectId,
                    fields: [
                        { path: '$.llm_type', value: llmType },
                        { path: '$.updated_on', value: timestamp }
                    ]
                };
                response = await AmeyaSettingsApi.update(JSON.stringify(payload), projectId, updateKey);
            }

            if (response?.status === 200) {
                fetchSettings()
                tostAlert('Settings updated successfully', 'success');
            }
        } catch (err) {
            console.error('Save Error:', err);
            tostAlert('Something went wrong', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>

                {loading && <LoadBar />}

                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography sx={styles.mainHeadingText}>
                        LLM Settings
                    </Typography>

                    <Box display="flex" alignItems="center">
                        <Button
                            sx={{ ...buttonStyles.primaryBtn, width: 100 }}
                            disabled={!projectId || loading}
                            onClick={handleSave}
                        >
                            <Typography sx={styles.btnText}>SAVE</Typography>
                        </Button>

                        <Button
                            sx={{ ...buttonStyles.secondaryBtn, width: 100, ml: 1 }}
                            onClick={fetchSettings}
                            disabled={loading}
                        >
                            <Typography sx={styles.btnText}>CANCEL</Typography>
                        </Button>
                    </Box>
                </Box>

                <Typography sx={{ ...styles.paraText, mt: 1 }}>
                    Configure LLM Settings
                </Typography>

                <Box
                    sx={{
                        border: '1px solid #D9D9D9',
                        borderRadius: 1,
                        p: 3,
                        mt: 2,
                        width: 600
                    }}
                >
                    <Typography sx={{ ...styles.paraText, mb: 1 }}>
                        Select LLM Type
                    </Typography>

                    <RadioGroup
                        value={llmType}
                        onChange={(e) => setLLMType(e.target.value)}
                        row
                    >
                        <FormControlLabel
                            value="gemini"
                            control={<Radio />}
                            label="Gemini"
                        />
                        <FormControlLabel
                            value="local"
                            control={<Radio />}
                            label="Local"
                        />
                    </RadioGroup>
                </Box>

            </Box>
        </Box>
    );
}

export default LLMSettings;